import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { editRole, getRole } from "../../../api/roles";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useTranslation } from "react-i18next";

const allResources = ["Sales", "Management", "Monitor"];
const allActions = ["create", "read", "update", "delete"];

const EditRole = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const res = await getRole(id);
        const role = res?.data;
        if (role) {
          setName(role.name);
          if (Array.isArray(role.permissions)) {
            const flatPermissions = role.permissions.flatMap((p) =>
              (p.actions || []).map((action) => `${action} ${p.resource}`)
            );
            setPermissions(flatPermissions);
          } else {
            setPermissions([]);
          }
        } else {
          alert("Role not found.");
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching role:", err);
        alert("Failed to fetch role.");
        setLoading(false);
      }
    };
    fetchRole();
  }, [id]);

  const togglePermission = (action, resource) => {
    const key = `${action} ${resource}`;
    if (permissions.includes(key)) {
      setPermissions(permissions.filter((p) => p !== key));
    } else {
      setPermissions([...permissions, key]);
    }
  };

  const handleFullAccess = (resource) => {
    const resourcePermissions = allActions.map(
      (action) => `${action} ${resource}`
    );

    // Check if all permissions for this resource are already selected
    const allSelected = resourcePermissions.every((perm) =>
      permissions.includes(perm)
    );

    if (allSelected) {
      // If all are selected, remove all permissions for this resource
      setPermissions(
        permissions.filter((perm) => !resourcePermissions.includes(perm))
      );
    } else {
      // If not all are selected, add all permissions for this resource
      setPermissions([...new Set([...permissions, ...resourcePermissions])]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const structuredPermissions = [];
      allResources.forEach((resource) => {
        const matchedActions = allActions.filter((action) =>
          permissions.includes(`${action} ${resource}`)
        );
        if (matchedActions.length > 0) {
          structuredPermissions.push({ resource, actions: matchedActions });
        }
      });

      await editRole(id, { name, permissions: structuredPermissions });
      alert("Role updated successfully");
      navigate("/roles");
    } catch (err) {
      console.error("Update failed:", err.response?.data || err.message);
      alert(err.response?.data?.message || err.message);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="container mt-4">
      <Breadcrumbs title={`${t("Edit")} ${t("Role")}`} />
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label>Role Name</label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <h4>Permissions</h4>
        <div className="permission-groups">
          {allResources.map((resource) => {
            const resourcePermissions = allActions.map(
              (action) => `${action} ${resource}`
            );
            const allSelected = resourcePermissions.every((perm) =>
              permissions.includes(perm)
            );

            return (
              <div key={resource} className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h5>{resource}</h5>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {allActions.map((action) => {
                    const key = `${action} ${resource}`;
                    const isSelected = permissions.includes(key);
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() => togglePermission(action, resource)}
                        className={`btn btn-sm ${
                          isSelected ? "btn-success" : "btn-outline-secondary"
                        }`}
                      >
                        {action}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => handleFullAccess(resource)}
                    className={`btn btn-sm ${
                      allSelected ? "btn-success" : "btn-outline-secondary"
                    }`}
                  >
                    Full Access
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <button type="submit" className="btn btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditRole;
